import type { FlowNode } from '@lighthouse/shared'
import {
    getAllCanBeUpstreamNodes,
    getUpstreamNodesOptions,
    type OpenLinkActionConfig,
    PAGE_LINK_OPTION,
    useAtomData
} from '@lighthouse/shared'
import { useNodesData } from '@xyflow/react'
import React, { useCallback, useMemo } from 'react'

import { lastPageOfStackAtom } from '@/atoms/page/state'
import { workflowListAtom } from '@/atoms/workflow/state'
import { useFlowContainerContext } from '@/contexts/FlowContext'
import { useCurrentAppId, useCurrentEnvId } from '@/hooks/useApplication'
import { useDataSourceList } from '@/hooks/useDataSource'
import { useGetPageParameterAndVariableOptions, useGetUserRecordOptions } from '@/hooks/usePage'

import * as CM from '../styles'
import { extractDataFn, textRichVariableName } from '../util'
import * as SC from './styles'

type OpenLinkCardProps = {
    config: OpenLinkActionConfig
}

const OpenLinkCard: React.FC<OpenLinkCardProps> = ({ config }) => {
    const { link } = config
    const { type } = useFlowContainerContext()
    const isInFlow = !!type
    const isNotAutomation = type !== 'automation'
    const appId = useCurrentAppId()
    const envId = useCurrentEnvId()
    const dataSourceList = useDataSourceList(appId, envId)
    const workflowList = useAtomData(workflowListAtom)
    const [stackId, pageId] = useAtomData(
        lastPageOfStackAtom,
        useCallback(s => [s?.stackId || '', s?.pageId || ''], [])
    )

    const userOption = useGetUserRecordOptions()
    const pageOptions = useGetPageParameterAndVariableOptions({ pageId })

    const extractData = useMemo(() => extractDataFn(link), [link])

    const nodes = useNodesData(extractData.nodeIds) as FlowNode[]

    const upstreamOptions = useMemo(
        () =>
            isInFlow
                ? getUpstreamNodesOptions({
                      workflowList,
                      dataSourceList,
                      upstreamNodes: getAllCanBeUpstreamNodes(nodes || []),
                      allUpstreamNodes: nodes
                  })
                : undefined,
        [isInFlow, workflowList, dataSourceList, nodes]
    )

    if (!extractData.objects.length) {
        return null
    }

    return (
        <CM.CardItemContainer>
            <CM.FieldItemContent>
                <CM.FieldItemTitle>
                    {extractData.objects.map(item => {
                        if (item.type === 'text') {
                            return <SC.Text key={item.value}>{item.value}</SC.Text>
                        }
                        const text = textRichVariableName({
                            userOption,
                            upstreamOptions,
                            pageOptions: isNotAutomation ? [PAGE_LINK_OPTION, ...pageOptions] : undefined,
                            data: item
                        })
                        return (
                            <SC.Text key={text} color="var(--color-purple-500)">
                                {text}
                            </SC.Text>
                        )
                    })}
                </CM.FieldItemTitle>
            </CM.FieldItemContent>
        </CM.CardItemContainer>
    )
}

export default OpenLinkCard
